'use client';

import { forwardRef, useCallback } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { AlertIcon } from './alert-icon';
import { AlertRootContext } from './context/alert-root.context';
import { alertRecipe } from './styles/alert-root.styles';
import type { AlertRootPropsInterface } from './types/alert-root.types';

/**
 * @since 10.0.0
 */
export const AlertRoot = forwardRef<HTMLDivElement, AlertRootPropsInterface>(
  (props: AlertRootPropsInterface, forwardedRef) => {
    const {
      tone = 'info',
      children,
      className,
      css: cssProp,
      isOpen,
      onClose,
      role = 'status',
      ...rest
    } = props;

    const [openState, setOpenState] = useControllableState({
      prop: isOpen,
      defaultProp: true,
    });

    const handleClose = useCallback(() => {
      setOpenState(false);
      onClose?.();
    }, [onClose, setOpenState]);

    if (!openState) {
      return null;
    }

    const styles = css(alertRecipe.raw({ tone }), cssProp);

    return (
      <AlertRootContext.Provider
        value={{
          tone,
          handleClose,
          isOpen: openState,
        }}
      >
        <div
          ref={forwardedRef}
          data-c11n-component="Alert.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          role={role}
          {...rest}
        >
          <AlertIcon />
          {children}
        </div>
      </AlertRootContext.Provider>
    );
  },
);

AlertRoot.displayName = 'Alert.Root';
